import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Box } from '@mui/material'
import Cookies from 'js-cookie'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaInfoCircle } from 'react-icons/fa'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import type { UnknownAction } from 'redux'

import popup from '@/assets/data/popup'
import Button from '@/components/Button'
import EditableField from '@/components/EditableField'
import { useAppSelector } from '@/hooks/redux'
import { useBreakpoints } from '@/hooks/useBreakpoints'
import {
  apiService,
  useCreateDentistMutation,
  useGetCaseDetailsQuery,
  useGetDentistQuery,
  useGetProfileQuery,
  useUpdateDentistNameMutation,
  useUpdateProjectNameMutation,
} from '@/store/apiService'
import { getCaseId } from '@/utils'
import { CASE_CARD_HEIGHT, DUAL_CANVAS_INCLUDED_PATHS } from '@/utils/constants'

import Dialog from './Dialog'
import Icon from './Icon'
import PopupContent from './PopupContent'
import SaveRecipeContainer from './SaveRecipeDropDown'

interface DentistResponse {
  id: string
  name: string
}

interface CaseDetails {
  name: string
  dentist?: DentistResponse
}

interface Profile {
  user: string
}

interface CaseInfoProps {
  disabled?: boolean
  onSaveAsImage: () => void
  onSaveAsPDF: () => void
  handleSaveToProject?: () => void
}

const CaseInfo: React.FC<CaseInfoProps> = ({
  disabled = false,
  onSaveAsImage,
  onSaveAsPDF,
  handleSaveToProject,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'caseInfoLayout' })
  const { t: translation } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const useQuery = () => {
    return new URLSearchParams(useLocation().search)
  }
  const query = useQuery()

  const updatedCaseId = query.get('caseId')
  if (updatedCaseId) {
    Cookies.set('case_id', updatedCaseId)
  }
  const { isDisplay1300, isDisplay1280 } = useBreakpoints()

  const breakPoint = DUAL_CANVAS_INCLUDED_PATHS.includes(
    location?.pathname || '',
  )
    ? true
    : isDisplay1300
  const locationPath = (location?.pathname || '')?.replace('/', '')

  const caseId = getCaseId() || ''
  const isLoggingOut = useAppSelector((state) => state.app.isLoggingOut)

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { data: caseDetails, refetch: refetchCaseDetails } =
    useGetCaseDetailsQuery<CaseDetails>(
      { caseId: caseId },
      { skip: !caseId || isLoggingOut },
    )

  const { data: profileData } = useGetProfileQuery<Profile>()
  const [updateDentistName] = useUpdateDentistNameMutation()
  const [updateProjectName] = useUpdateProjectNameMutation()
  const [createDentist] = useCreateDentistMutation()

  const [projectReName, setProjectReName] = useState<string>()
  const [dentistReName, setDentistReName] = useState<string>()

  const popupData = useMemo(() => {
    const key = location?.pathname?.split('/')?.filter(Boolean).join('-')
    return popup?.[key as keyof typeof popup] || {}
  }, [location?.pathname])
  const { data: dentistList } = useGetDentistQuery({})

  const excludedPaths = [
    '/all-in-one',
    '/shade-guide-ai',
    '/matisse-lite/dentin_enamel',
  ]

  const isExcluded =
    excludedPaths.includes(location.pathname) ||
    location.pathname.startsWith('/digital-try-in')

  const handleProjectNameSave = (value: string, closeEdit: () => void) => {
    updateProjectName({
      name: value.trim(),
      caseId,
      case_type: 'manual',
    })
      .unwrap()
      .then((response: { name: string }) => {
        setProjectReName(response.name)
        refetchCaseDetails()
      })
      .then(() => closeEdit())
  }

  const handleDentistNameSave = async (
    value: string,
    closeEdit: () => void,
  ) => {
    if (!profileData?.user) return

    try {
      const existingDentist = dentistList?.find(
        (dentist) =>
          dentist.name.trim().toLowerCase() === value.trim().toLowerCase(),
      )

      let dentistId = existingDentist?.id
      let dentistName = value

      if (!dentistId) {
        const dentistResponse = (await createDentist({
          name: value,
          user: profileData.user,
        }).unwrap()) as DentistResponse
        // eslint-disable-next-line require-atomic-updates
        dentistId = dentistResponse.id
        dentistName = dentistResponse.name
      }

      await updateDentistName({
        caseId,
        name: caseDetails?.name || '',
        dentist: dentistId,
      }).unwrap()

      dispatch(
        apiService.util.updateQueryData(
          'getCaseDetails',
          caseId,
          (draft: CaseDetails) => {
            draft.dentist = {
              id: dentistId!,
              name: dentistName,
            }
          },
        ) as unknown as UnknownAction,
      )

      setDentistReName(dentistName)
      closeEdit()
    } catch (error) {
      console.error('Failed to update dentist:', error)
    }
  }

  return (
    <Box
      display="flex"
      alignItems={breakPoint ? 'center' : 'flex-start'}
      justifyContent="space-between"
      px={4}
      pt={1}
      pb={2}
      minHeight={`${CASE_CARD_HEIGHT}px`}
      flexGrow={1}
      gap={breakPoint ? 4 : 2}
      flexDirection={breakPoint ? 'row' : 'column'}
    >
      <Box
        display="flex"
        alignItems={breakPoint ? 'center' : 'flex-start'}
        gap={2}
        flexDirection={breakPoint ? 'row' : 'column'}
        flexWrap={'wrap'}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            fontSize="1.5rem"
            fontWeight={700}
            letterSpacing="-0.03rem"
            lineHeight="1.75rem"
          >
            {/* {RECIPE_TITLE[locationPath as keyof typeof RECIPE_TITLE]} */}
            {translation(`recipeTitles.${locationPath}`)}
          </Box>
          <Dialog
            triggerElement={
              <Box
                display="flex"
                sx={{ cursor: 'pointer' }}
                onClick={() => setIsDialogOpen(true)}
              >
                <Icon icon={FaInfoCircle} size={24} />
              </Box>
            }
            isOpen={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            title={translation(`${popupData}.title`)}
            content={
              <PopupContent
                popupData={translation(`${popupData}.data`, {
                  returnObjects: true,
                })}
              />
            }
            actionButtons={
              <>
                <Button
                  variant="outlined"
                  label={translation('buttons.cancel')}
                  onClick={() => setIsDialogOpen(false)}
                  textTransform="uppercase"
                />
                <Button
                  label={translation('buttons.done')}
                  onClick={() => setIsDialogOpen(false)}
                  textTransform="uppercase"
                />
              </>
            }
          />
        </Box>
        <Box gap={2} display={'flex'}>
          {caseDetails?.case_type !== 'auto' && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              label={isDisplay1280 ? t('backToProject') : t('back')}
              startIcon={<ArrowBackIcon color="inherit" />}
              onClick={async () => navigate(`/projects/${caseId}`)}
              sx={{
                padding: '4px 10px',
              }}
              textTransform="uppercase"
            />
          )}
          {!isExcluded && (
            <SaveRecipeContainer
              disabled={disabled}
              onSaveAsImage={onSaveAsImage}
              onSaveAsPDF={onSaveAsPDF}
              handleSaveToProject={handleSaveToProject}
            />
          )}
        </Box>
      </Box>
      <Box display="flex" alignItems="end" flexDirection="column" gap={0.5}>
        <EditableField
          label={t('project')}
          value={projectReName || caseDetails?.name || ''}
          onSave={handleProjectNameSave}
          dentistList={dentistList}
          caseDetails={caseDetails}
        />
        <EditableField
          label={t('dentist')}
          value={dentistReName || caseDetails?.dentist?.name || ''}
          onSave={handleDentistNameSave}
          dentistList={dentistList}
          caseDetails={caseDetails}
        />
      </Box>
    </Box>
  )
}

export default CaseInfo
